// Page library types + default primary page keys for proposal packages.
// Relative imports so server handlers can share these helpers.
// Body normalize / default helpers live in proposalPageBodyOps.ts.

import type { OpportunityFormatKey } from "../../types/opportunity.js";
import type { IntentSignatureParty } from "./proposalIntentLayout.js";
import type { ProposalSandboxBlock } from "./proposalSandboxBlocks.js";

export type {
  IntentFillField,
  IntentFillKind,
  IntentPageLayout,
  IntentSignatureParty,
} from "./proposalIntentLayout.js";
export type {
  ProposalSandboxBlock,
  ProposalSandboxBlockType,
} from "./proposalSandboxBlocks.js";
export {
  defaultIntentSandboxBlocks,
  migrateIntentSandboxBlocks as normalizeProposalSandboxBlocks,
  textPartsFromSandboxBlocks,
} from "./proposalSandboxStarters.js";
export {
  defaultBodyForArchetype,
  sandboxBlocksFromPageBody,
  intentBlocksFromBody,
  intentLayoutFromBody,
  bodyFromOverrides,
  sectionsFromPageBody,
  normalizeProposalPageBody,
  archetypeToPageKind,
  pageKindToArchetype,
  type SectionOverrideLike,
} from "./proposalPageBodyOps.js";

export const PROPOSAL_PAGE_ARCHETYPES = [
  "cover_letter",
  "cost_breakdown",
  "payment_schedule",
  "intent_to_purchase",
] as const;

export type ProposalPageArchetype = (typeof PROPOSAL_PAGE_ARCHETYPES)[number];

export type ProposalPageSectionPart = {
  text: string;
  included: boolean;
};

export type ProposalPageBodyCover = {
  body: ProposalPageSectionPart;
  blocks?: ProposalSandboxBlock[];
};
export type ProposalPageBodyCost = {
  intro: ProposalPageSectionPart;
  footer: ProposalPageSectionPart;
  blocks?: ProposalSandboxBlock[];
};
export type ProposalPageBodyPayment = {
  intro: ProposalPageSectionPart;
  footer: ProposalPageSectionPart;
  blocks?: ProposalSandboxBlock[];
};
export type ProposalPageBodyIntent = {
  preamble: ProposalPageSectionPart;
  signature_block: ProposalPageSectionPart;
  blocks?: ProposalSandboxBlock[];
  /** @deprecated Prefer blocks; kept for older jsonb rows. */
  show_customer_sample?: boolean;
  /** @deprecated Prefer blocks; kept for older jsonb rows. */
  signature_parties?: IntentSignatureParty[];
};

export type ProposalPageBody =
  | ProposalPageBodyCover
  | ProposalPageBodyCost
  | ProposalPageBodyPayment
  | ProposalPageBodyIntent;

export type ProposalPage = {
  id: string;
  organization_id: string;
  format_key: OpportunityFormatKey;
  product_type: string;
  page_key: string;
  label: string;
  archetype: ProposalPageArchetype;
  body: ProposalPageBody;
  sort_order: number;
  created_at: string;
  updated_at: string;
};

export const PRIMARY_PAGE_KEYS: Record<ProposalPageArchetype, string> = {
  cover_letter: "primary_cover_letter",
  cost_breakdown: "primary_cost_breakdown",
  payment_schedule: "primary_payment_schedule",
  intent_to_purchase: "primary_intent_to_purchase",
};

export const PRIMARY_PAGE_LABELS: Record<ProposalPageArchetype, string> = {
  cover_letter: "Cover letter (Primary)",
  cost_breakdown: "Cost breakdown (Primary)",
  payment_schedule: "Payment schedule (Primary)",
  intent_to_purchase: "Intent / signature (Primary)",
};

export const PROPOSAL_PAGE_ARCHETYPE_LABELS: Record<ProposalPageArchetype, string> = {
  cover_letter: "Cover letter",
  cost_breakdown: "Cost breakdown",
  payment_schedule: "Payment schedule",
  intent_to_purchase: "Intent / signature",
};

export function isProposalPageArchetype(v: unknown): v is ProposalPageArchetype {
  return (
    typeof v === "string" &&
    (PROPOSAL_PAGE_ARCHETYPES as readonly string[]).includes(v)
  );
}

export function isValidProposalPageKey(key: string): boolean {
  return /^[a-z][a-z0-9_]{0,47}$/.test(key.trim());
}
